import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { api, apiErr } from "../lib/apiClient";
import { useI18n } from "../i18n/I18nContext";
import { Plus, Pencil, Building2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY = { name: "", business_name: "", address: "", city: "", province: "", zip_code: "", email: "", phone: "", contact: "", notes: "", status: "active" };
const FIELDS = ["name", "business", "contact", "address", "city", "province", "zip", "email", "phone"];
const MAP = { name: "name", business: "business_name", contact: "contact", address: "address", city: "city", province: "province", zip: "zip_code", email: "email", phone: "phone" };

export default function Structures() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/structures").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (s) => {
    if (!window.confirm(`Eliminare definitivamente la struttura "${s.name}" e TUTTI i dati collegati (router, prenotazioni, profili, utenti Wi-Fi, sessioni, log)?\n\nL'operazione è IRREVERSIBILE.`)) return;
    try {
      const { data } = await api.delete(`/structures/${s.id}`);
      const tot = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Struttura eliminata (${tot} record collegati rimossi)`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      if (editId) await api.put(`/structures/${editId}`, form);
      else await api.post("/structures", form);
      toast.success(t("common.save") + " ✓");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="structures-page">
      <PageHeader title={t("structures.title")} action={
        <button data-testid="new-structure-btn" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2.5 text-sm font-semibold transition-colors">
          <Plus className="h-4 w-4" /> {t("structures.new")}
        </button>} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} data-testid={`structure-card-${s.id}`} className="rounded-xl border border-[#1F2937] bg-[#111827] p-5 hover:border-blue-600/30 transition-colors">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-lg bg-blue-500/10 ring-1 ring-blue-500/20 flex items-center justify-center"><Building2 className="h-5 w-5 text-blue-400" /></div>
              <StatusBadge status={s.status === "active" ? "active_st" : "inactive"} label={s.status} />
            </div>
            <h3 className="font-display font-semibold mt-4">{s.name}</h3>
            <p className="text-xs text-slate-500">{s.business_name}</p>
            <div className="mt-3 space-y-1 text-sm text-slate-400">
              <div>{s.address}, {s.zip_code} {s.city} ({s.province})</div>
              <div className="text-xs">{s.contact} · {s.phone}</div>
              <div className="text-xs font-mono text-blue-400">{s.email}</div>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <button data-testid={`edit-structure-${s.id}`} onClick={() => { setForm({ ...EMPTY, ...s }); setEditId(s.id); setOpen(true); }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-blue-300 transition-colors">
                <Pencil className="h-3.5 w-3.5" /> {t("common.edit")}
              </button>
              <button data-testid={`delete-structure-${s.id}`} onClick={() => del(s)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-400 hover:text-rose-300 transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Elimina
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? t("common.edit") : t("structures.new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <div key={f} className={f === "name" || f === "business" || f === "address" ? "col-span-2" : ""}>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t(`structures.${f}`)}</label>
                <input data-testid={`structure-input-${f}`} value={form[MAP[f]] || ""} onChange={(e) => setForm({ ...form, [MAP[f]]: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-slate-400 hover:text-slate-100">{t("common.cancel")}</button>
            <button data-testid="save-structure-btn" onClick={save} className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-semibold">{t("common.save")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
